//src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  ChevronRight
} from 'lucide-react';
import { debug } from '../utils/debugUtils';

// Import shadcn/ui components
import { Button } from './ui/button';
import { Input } from './ui/input';

function FooterComponent() {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // TODO: Add newsletter subscription logic
    // Note: Using debug utility for user action tracking
    debug.log('Newsletter subscription submitted');
  };

  return (
    <footer className="mt-auto">
      {/* Main Footer */}
      <div className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Column 1: Company Info Section */}
            <div className="lg:col-span-1">
              <div className="company-info">
                {/* Logo and Company Name */}
                <div className="flex items-center mb-4">
                  <img
                    src="/assets/logo no background.png"
                    alt="Printereum Logo"
                    className="w-12 h-12 object-contain"
                  />
                  <h3 className="ml-3 text-xl font-bold text-[#ed1d35]">
                    Printereum
                  </h3>
                </div>
                
                {/* Company Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  We're a family business committed to excellence in printing. Our dedication to quality and customer service has made us a trusted partner for all printing needs.
                </p>
                
                {/* Social Links */}
                <div className="flex gap-3">
                  <a
                    href="https://facebook.com/printereum"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 hover:bg-[#ed1d35] hover:text-white transition-all duration-300 hover:-translate-y-1"
                    aria-label="Facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Facebook size={16} />
                  </a>
                  <a
                    href="https://twitter.com/printereum"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 hover:bg-[#ed1d35] hover:text-white transition-all duration-300 hover:-translate-y-1"
                    aria-label="Twitter"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Twitter size={16} />
                  </a>
                  <a
                    href="https://instagram.com/printereum"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 hover:bg-[#ed1d35] hover:text-white transition-all duration-300 hover:-translate-y-1"
                    aria-label="Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram size={16} />
                  </a>
                  <a
                    href="https://linkedin.com/company/printereum"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 hover:bg-[#ed1d35] hover:text-white transition-all duration-300 hover:-translate-y-1"
                    aria-label="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Linkedin size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* Column 2: Quick Links Section */}
            <div className="lg:col-span-1">
              <div className="footer-links">
                <div className="mb-3">
                  <h4 className="text-[#ed1d35] font-semibold mb-2 text-sm">Quick Links</h4>
                  <div className="w-8 h-0.5 bg-[#ed1d35] rounded"></div>
                </div>
                <ul className="space-y-2">
                  {[
                    { to: '/quotes', label: 'Quotes' },
                    { to: '/orders', label: 'Orders' },
                    { to: '/customers', label: 'Customers' },
                    { to: '/email_display', label: 'Emails' },
                    { to: '/onboarding', label: 'Onboarding' }
                  ].map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="group flex items-center text-gray-700 hover:text-[#ed1d35] transition-all duration-200 hover:translate-x-1 text-sm"
                      >
                        <ChevronRight 
                          size={14} 
                          className="text-[#ed1d35] mr-1 group-hover:translate-x-1 transition-transform duration-200" 
                        />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Column 3: Company Links Section */}
            <div className="lg:col-span-1">
              <div className="footer-links">
                <div className="mb-3">
                  <h4 className="text-[#ed1d35] font-semibold mb-2 text-sm">Company</h4>
                  <div className="w-8 h-0.5 bg-[#ed1d35] rounded"></div>
                </div>
                <ul className="space-y-2">
                  {[
                    { to: '/about', label: 'About Us' },
                    { to: '/services', label: 'Services' },
                    { to: '/team', label: 'Our Team' },
                    { to: '/careers', label: 'Careers' }
                  ].map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="group flex items-center text-gray-700 hover:text-[#ed1d35] transition-all duration-200 hover:translate-x-1 text-sm"
                      >
                        <ChevronRight 
                          size={14} 
                          className="text-[#ed1d35] mr-1 group-hover:translate-x-1 transition-transform duration-200" 
                        />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Column 4: Stay Updated & Contact Information */}
            <div className="lg:col-span-1">
              <div className="footer-links">
                <div className="mb-3">
                  <h4 className="text-[#ed1d35] font-semibold mb-2 text-sm">Stay Updated</h4>
                  <div className="w-8 h-0.5 bg-[#ed1d35] rounded"></div>
                </div>
                
                {/* Newsletter Description */}
                <p className="text-gray-600 text-sm mb-4">
                  Subscribe to our newsletter for the latest news and printing tips.
                </p>
                
                {/* Newsletter Subscription Form */}
                <form onSubmit={handleNewsletterSubmit} className="mb-6">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Your email address"
                      className="flex-1 text-sm"
                      required
                    />
                    <Button 
                      type="submit" 
                      size="sm"
                      className="bg-[#ed1d35] hover:bg-[#c4162b] text-white px-4"
                    >
                      Subscribe
                    </Button>
                  </div>
                </form>

                {/* Contact Details */}
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700 text-sm">
                    <Mail size={14} className="text-[#ed1d35] mr-2 flex-shrink-0" />
                    <a 
                      href="mailto:hatamian@printereum.com"
                      className="hover:text-[#ed1d35] transition-colors duration-200"
                    >
                      hatamian@printereum.com
                    </a>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm">
                    <Phone size={14} className="text-[#ed1d35] mr-2 flex-shrink-0" />
                    <a 
                      href="tel:(702)966-1000"
                      className="hover:text-[#ed1d35] transition-colors duration-200"
                    >
                      (702) 966-1000
                    </a>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm">
                    <MapPin size={14} className="text-[#ed1d35] mr-2 flex-shrink-0" />
                    <span>Las Vegas, NV</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-[#ed1d35] text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs opacity-90 mb-1 md:mb-0">
              © {new Date().getFullYear()} Printereum. All rights reserved.
            </p>
            <ul className="flex gap-4 text-xs">
              <li>
                <Link 
                  to="/privacy" 
                  className="opacity-90 hover:opacity-100 transition-opacity duration-200"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="opacity-90 hover:opacity-100 transition-opacity duration-200"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterComponent;